/**
 * Wave 5 (2026-09-11) — Average-End-Area (AEA) earthwork cut/fill
 * estimator. Project-scoped (not tied to a single measurement): a
 * superintendent enters station-to-station cross-sectional areas
 * (existing-ground minus design-grade, signed: +cut / -fill) straight
 * off the cross-section sheets, and gets a real textbook AEA cut/fill
 * volume — no invented shrink/swell factor, no synthetic geometry.
 * See backend/services/earthwork_calc.py for the formula.
 */
import { useEffect, useState } from "react";
import { X, Plus, Trash2, Loader2, Mountain, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";

function fmt(n, d = 2) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
}

function emptyStation(isFirst) {
  return { station: "", distance_from_prev: isFirst ? "0" : "", signed_area: "" };
}

function StationForm({ initial, onSave, onCancel, saving, error }) {
  const [name, setName] = useState(initial?.name || "");
  const [stations, setStations] = useState(
    initial?.stations?.length
      ? initial.stations.map((s) => ({
          station: s.station, distance_from_prev: String(s.distance_from_prev), signed_area: String(s.signed_area),
        }))
      : [emptyStation(true), emptyStation(false)],
  );

  const updateStation = (i, key, val) => {
    setStations((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  };
  const addStation = () => setStations((prev) => [...prev, emptyStation(false)]);
  const removeStation = (i) => setStations((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid="earthwork-station-form">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Estimate name, e.g. STA 10+00 to 14+00 mainline cut"
        className="w-full px-2 py-1.5 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono"
        data-testid="earthwork-name-input"
      />
      <div className="space-y-1">
        <div className="grid grid-cols-[1.2fr_1fr_1fr_28px] gap-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] px-0.5">
          <span>Station</span>
          <span>Interval (ft)</span>
          <span>Area (sf) +cut/-fill</span>
          <span />
        </div>
        {stations.map((s, i) => (
          <div key={i} className="grid grid-cols-[1.2fr_1fr_1fr_28px] gap-1" data-testid={`earthwork-station-row-${i}`}>
            <input
              value={s.station} onChange={(e) => updateStation(i, "station", e.target.value)}
              placeholder="10+00" className="px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono"
              data-testid={`earthwork-station-name-${i}`}
            />
            <input
              value={s.distance_from_prev} onChange={(e) => updateStation(i, "distance_from_prev", e.target.value)}
              inputMode="decimal" disabled={i === 0} placeholder={i === 0 ? "0" : "100"}
              className="px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono disabled:opacity-40"
              data-testid={`earthwork-station-distance-${i}`}
            />
            <input
              value={s.signed_area} onChange={(e) => updateStation(i, "signed_area", e.target.value)}
              inputMode="decimal" placeholder="120"
              className="px-1.5 py-1 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded text-[11px] font-mono"
              data-testid={`earthwork-station-area-${i}`}
            />
            <button
              onClick={() => removeStation(i)} disabled={stations.length <= 2}
              className="flex items-center justify-center rounded text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] disabled:opacity-30"
              data-testid={`earthwork-station-remove-${i}`} title="Remove station"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addStation}
        className="w-full flex items-center justify-center gap-1 px-2 py-1 rounded border border-dashed border-[var(--fop-border-hi)] text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] hover:border-[var(--fop-primary)]"
        data-testid="earthwork-add-station"
      >
        <Plus size={11} /> Add station
      </button>
      {error && <div className="text-[10px] text-[var(--st-escalated)] font-mono" data-testid="earthwork-form-error">{error}</div>}
      <div className="flex items-center gap-1.5 pt-1">
        <button
          onClick={() => {
            const parsed = stations.map((s) => ({
              station: s.station.trim(),
              distance_from_prev: parseFloat(s.distance_from_prev || "0"),
              signed_area: parseFloat(s.signed_area),
            }));
            onSave(name.trim(), parsed);
          }}
          disabled={saving}
          className="flex-1 px-2 py-1.5 rounded bg-[var(--fop-primary)] text-[var(--fop-on-primary)] text-[10px] font-mono uppercase tracking-wider flex items-center justify-center gap-1 disabled:opacity-50"
          data-testid="earthwork-save"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : "Compute & save"}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 rounded text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]" data-testid="earthwork-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}

function EstimateRow({ est, onDeleted, onUpdated }) {
  const c = est.computed;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const del = async () => {
    if (!window.confirm(`Delete earthwork estimate "${est.name}"?`)) return;
    try {
      await api.delete(`/earthwork-estimates/${est.id}`);
      onDeleted(est.id);
    } catch {
      confirm.saveFailed("Couldn't delete earthwork estimate");
    }
  };

  const save = async (name, stations) => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.patch(`/earthwork-estimates/${est.id}`, { name, stations });
      onUpdated(data);
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save — check station values");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-2 rounded bg-[var(--fop-surface-2)] border border-[var(--fop-border)] text-[11px] font-mono" data-testid={`earthwork-row-${est.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[var(--fop-text)] font-semibold truncate" data-testid={`earthwork-name-${est.id}`}>{est.name}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing((v) => !v)} data-testid={`earthwork-edit-toggle-${est.id}`} title="Edit">
            <Pencil size={12} className="text-[var(--fop-text-3)] hover:text-[var(--fop-primary)]" />
          </button>
          <button onClick={del} data-testid={`earthwork-delete-${est.id}`} title="Delete">
            <Trash2 size={12} className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]" />
          </button>
        </div>
      </div>
      {editing ? (
        <div className="mt-2">
          <StationForm
            initial={{ name: est.name, stations: est.stations }}
            onSave={save} onCancel={() => setEditing(false)} saving={saving} error={err}
          />
        </div>
      ) : c ? (
        <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[var(--fop-text-2)]" data-testid={`earthwork-computed-${est.id}`}>
          <span>Total cut</span><span className="text-right">{fmt(c.total_cut_cy)} cy</span>
          <span>Total fill</span><span className="text-right">{fmt(c.total_fill_cy)} cy</span>
          <span className="font-semibold text-[var(--fop-text)]">Net ({c.net_direction})</span>
          <span className="text-right font-semibold text-[var(--fop-text)]">{fmt(Math.abs(c.net_cy))} cy</span>
          <span className="col-span-2 text-[9px] text-[var(--fop-text-3)] mt-0.5">{c.segments.length} station-to-station segment{c.segments.length !== 1 ? "s" : ""}</span>
        </div>
      ) : (
        <div className="mt-1.5 text-[var(--st-escalated)]" data-testid={`earthwork-calc-error-${est.id}`}>{est.calc_error || "Couldn't compute"}</div>
      )}
    </div>
  );
}

export default function EarthworkEstimatePanel({ projectId, onClose }) {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`/projects/${projectId}/earthwork-estimates`)
      .then((r) => setEstimates(r.data?.estimates || []))
      .catch(() => setEstimates([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const create = async (name, stations) => {
    if (!name) { setErr("Name is required"); return; }
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.post(`/projects/${projectId}/earthwork-estimates`, {
        project_id: projectId, name, stations,
      });
      setEstimates((prev) => [data, ...prev]);
      setShowForm(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save — check station values");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="earthwork-panel-overlay">
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg bg-[var(--fop-surface)] border border-[var(--fop-border)] p-4" data-testid="earthwork-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Mountain size={16} className="text-[var(--fop-primary)]" />
            <h3 className="text-[13px] font-semibold text-[var(--fop-text)]">Earthwork — Average End Area</h3>
          </div>
          <button onClick={onClose} data-testid="earthwork-panel-close" className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
            <X size={16} />
          </button>
        </div>
        <p className="text-[10px] text-[var(--fop-text-3)] mb-3 leading-relaxed">
          Enter station-to-station cross-sectional areas from your cross-section
          sheets (existing ground minus design grade). Positive = cut, negative
          = fill. Volume = (A1+A2)/2 × interval distance per segment, split at
          the true zero-crossing when a segment transitions cut→fill.
        </p>
        {loading ? (
          <div className="text-[10px] text-[var(--fop-text-3)] font-mono">Loading…</div>
        ) : (
          <div className="space-y-2">
            {estimates.map((est) => (
              <EstimateRow
                key={est.id} est={est}
                onDeleted={(id) => setEstimates((prev) => prev.filter((e) => e.id !== id))}
                onUpdated={(updated) => setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))}
              />
            ))}
          </div>
        )}
        {showForm ? (
          <div className="mt-3 p-2 rounded bg-[var(--fop-surface-3)] border border-[var(--fop-border)]">
            <StationForm onSave={create} onCancel={() => setShowForm(false)} saving={saving} error={err} />
          </div>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            className="mt-3 w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-dashed border-[var(--fop-border-hi)] text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] hover:border-[var(--fop-primary)]"
            data-testid="earthwork-add-estimate"
          >
            <Plus size={11} /> New cut/fill estimate
          </button>
        )}
      </div>
    </div>
  );
}
